/*
 * Free-gift engine — biotique cart drawer
 *
 * Watches every cart mutation on the page (fetch + XHR, any source: drawer,
 * grid steppers, PDP forms, app widgets) and keeps the cart's free-gift lines
 * in step with the tier rules the merchant set on the Cart drawer section
 * ("Free gift tier" blocks). Rules are read from the JSON island
 * #CartDrawer-FreeGiftData rendered by sections/cart-drawer.liquid:
 *
 *   { enabled, tiers: [{ id, variantIds: [..], threshold, label }] }  (threshold: cents)
 *
 * A tier can gift SEVERAL products (variantIds, up to 3 from the theme
 * editor) — every listed variant is added as its own gift line when the tier
 * unlocks, and all of them leave together when it locks again.
 *
 * INSTANT reconcile: most cart writes (/cart/change, /cart/update) answer with
 * the FULL cart JSON, so the wrapper reads that response body and reconciles
 * immediately — no extra /cart.js round trip, no long debounce. The gift write
 * itself requests sections=cart-drawer,cart-icon-bubble and its response HTML
 * is painted straight into the drawer via window.__cartDrawerApplySections.
 * Net effect: the gift appears/disappears one request (~200ms) after the cart
 * crosses a tier value, with the open drawer updating in place.
 *
 * Contract with the rest of the theme:
 *  - A gift line is any line carrying the `_free_gift` line-item property; its
 *    value is the tier block id, so two tiers can gift the SAME product as two
 *    separate lines, and each line maps back to exactly one rule.
 *  - Eligibility is judged PER TIER on the subtotal EXCLUDING gift lines (so
 *    a gift that isn't priced ₹0 can never unlock itself), optionally
 *    narrowed by the tier's includeTag / excludeTag (exclude wins). /cart.js
 *    items don't carry product tags, so tags are fetched once per product
 *    from /products/{handle}.js and cached (memory + sessionStorage) — and
 *    ONLY when at least one tier actually uses a tag rule.
 *  - Every request this engine sends carries the X-Cart-Free-Gift header;
 *    custom-cart-drawer.js sees it and skips the "Product Added" pill/toast +
 *    external-add refresh (this engine paints the drawer itself).
 *  - Tiers are independent: a cart above two thresholds holds both gifts.
 */
(function () {
  "use strict";

  var GIFT_PROP = "_free_gift";
  var GIFT_HEADER = "X-Cart-Free-Gift";
  var SYNC_COALESCE_MS = 60; // just enough to fold a burst of writes into one pass
  var FAIL_COOLDOWN_MS = 60000; // don't hammer a sold-out gift variant
  var MAX_CHAIN = 4; // consecutive mutating syncs allowed before backing off

  var syncTimer = null;
  var syncInFlight = false;
  var resyncQueued = false;
  var chainCount = 0;
  var failedAdds = {}; // variantId -> last failure timestamp
  var pendingSnapshot = null; // freshest full-cart JSON seen while coalescing

  function getConfig() {
    var node = document.getElementById("CartDrawer-FreeGiftData");
    if (!node) return null;
    var cfg;
    try {
      cfg = JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
    if (!cfg || !Array.isArray(cfg.tiers)) return null;
    var tiers = cfg.enabled
      ? cfg.tiers.filter(function (t) {
          return (
            t &&
            Array.isArray(t.variantIds) &&
            t.variantIds.length > 0 &&
            Number(t.threshold) > 0
          );
        })
      : []; // feature switched off → still sweep out any lingering gift lines
    // Dedupe variant ids WITHIN a tier — the same product picked twice in one
    // tier's list would otherwise add two lines that then fight the reconcile
    // (the duplicate line is judged stale and removed, every sync). The same
    // product across two DIFFERENT tiers is fine: distinct property values.
    tiers.forEach(function (t) {
      var seen = {};
      t.variantIds = t.variantIds.filter(function (vid) {
        var key = String(vid);
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
    });
    return { tiers: tiers };
  }

  function looksLikeFullCart(body) {
    return Boolean(
      body && typeof body.item_count === "number" && Array.isArray(body.items)
    );
  }

  // ---- Product-tag lookup (include/exclude rules) ---------------------------
  // Tag matching mirrors the section's Liquid (`tags contains 'x'`): exact,
  // case-sensitive. Cached per product for the session.

  var TAG_CACHE_KEY = "cd-free-gift-tags";
  var tagCache = (function () {
    try {
      return JSON.parse(window.sessionStorage.getItem(TAG_CACHE_KEY)) || {};
    } catch (e) {
      return {};
    }
  })();

  function saveTagCache() {
    try {
      window.sessionStorage.setItem(TAG_CACHE_KEY, JSON.stringify(tagCache));
    } catch (e) {
      // Storage full/blocked — in-memory cache still works for this page.
    }
  }

  function tiersUseTags(tiers) {
    return tiers.some(function (t) {
      return Boolean(t.includeTag) || Boolean(t.excludeTag);
    });
  }

  // Fill the tag cache for every non-gift line. Skipped entirely when no tier
  // has a tag rule, so carts without tag rules never pay the extra requests.
  async function ensureTags(cart, tiers) {
    if (!tiersUseTags(tiers)) return;
    var seen = {};
    var fetches = [];
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties[GIFT_PROP]) return;
      var pid = String(item.product_id);
      if (tagCache[pid] || seen[pid] || !item.handle) return;
      seen[pid] = true;
      fetches.push(
        fetch("/products/" + item.handle + ".js", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (product) {
            if (product && Array.isArray(product.tags)) {
              tagCache[pid] = product.tags;
            }
          })
          .catch(function () {})
      );
    });
    if (fetches.length) {
      await Promise.all(fetches);
      saveTagCache();
    }
  }

  // Does this line count toward this tier's cart value? Unknown tags (lookup
  // failed) can't be verified: the item is dropped when an include tag is
  // required, and kept when only an exclude tag is set.
  function itemCountsForTier(item, tier) {
    if (!tier.includeTag && !tier.excludeTag) return true;
    var tags = tagCache[String(item.product_id)];
    if (tier.excludeTag && tags && tags.indexOf(tier.excludeTag) !== -1) return false;
    if (tier.includeTag) return Boolean(tags && tags.indexOf(tier.includeTag) !== -1);
    return true;
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    }).then(function (r) {
      if (!r.ok) throw new Error("cart fetch failed");
      return r.json();
    });
  }

  function giftPost(url, payload) {
    var headers = { "Content-Type": "application/json", Accept: "application/json" };
    headers[GIFT_HEADER] = "1";
    // Ask Shopify to render the drawer in the SAME response so the repaint
    // needs no follow-up fetch.
    payload.sections = ["cart-drawer", "cart-icon-bubble"];
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: headers,
      body: JSON.stringify(payload),
    });
  }

  // Decide what has to change to make the cart match the rules.
  function buildPlan(cart, tiers) {
    var normalItems = [];
    var giftLines = [];
    (cart.items || []).forEach(function (item) {
      var tierId = item.properties && item.properties[GIFT_PROP];
      if (tierId) {
        giftLines.push({
          key: item.key,
          tierId: String(tierId),
          variantId: item.variant_id,
          quantity: item.quantity,
        });
      } else {
        normalItems.push(item);
      }
    });

    // Per-tier eligible total — each tier applies its own include/exclude tag.
    var activeTiers = tiers.filter(function (t) {
      var eligibleTotal = 0;
      normalItems.forEach(function (item) {
        if (itemCountsForTier(item, t)) eligibleTotal += item.final_line_price;
      });
      return eligibleTotal >= Number(t.threshold);
    });

    var plan = { removes: [], qtyFixes: [], adds: [] };
    var claimed = {}; // "tierId:variantId" -> true once a valid line owns it

    giftLines.forEach(function (line) {
      var tier = null;
      for (var i = 0; i < activeTiers.length; i++) {
        if (String(activeTiers[i].id) === line.tierId) {
          tier = activeTiers[i];
          break;
        }
      }
      // Stale line: tier locked again / removed / product no longer in the
      // tier's list / duplicate of an already-claimed variant.
      var inTier =
        tier &&
        tier.variantIds.some(function (vid) {
          return String(vid) === String(line.variantId);
        });
      var claimKey = line.tierId + ":" + String(line.variantId);
      if (!inTier || claimed[claimKey]) {
        plan.removes.push(line);
        return;
      }
      claimed[claimKey] = true;
      if (line.quantity !== 1) plan.qtyFixes.push(line);
    });

    // Every unlocked tier adds EACH of its listed products that isn't in yet.
    activeTiers.forEach(function (tier) {
      tier.variantIds.forEach(function (vid) {
        if (claimed[String(tier.id) + ":" + String(vid)]) return;
        var failedAt = failedAdds[String(vid)];
        if (failedAt && Date.now() - failedAt < FAIL_COOLDOWN_MS) return;
        plan.adds.push({ tierId: String(tier.id), variantId: vid });
      });
    });

    return plan;
  }

  // Run the plan. Removes/locks first so an add can never race a shifting cart.
  // Returns { mutated, last, countDelta } — `last` is the final successful
  // response body (carries the freshest sections render + often item_count).
  async function applyPlan(plan) {
    var mutated = false;
    var last = null;
    var countDelta = 0;

    async function run(url, payload, onOk) {
      var res = await giftPost(url, payload);
      if (!res.ok) return false;
      mutated = true;
      try {
        last = await res.json();
      } catch (e) {
        last = null;
      }
      if (onOk) onOk();
      return true;
    }

    for (var i = 0; i < plan.removes.length; i++) {
      var line = plan.removes[i];
      await run("/cart/change.js", { id: line.key, quantity: 0 }, function () {
        countDelta -= line.quantity;
      });
    }
    for (var j = 0; j < plan.qtyFixes.length; j++) {
      var fix = plan.qtyFixes[j];
      await run("/cart/change.js", { id: fix.key, quantity: 1 }, function () {
        countDelta += 1 - fix.quantity;
      });
    }
    function toAddItem(add) {
      var props = {};
      props[GIFT_PROP] = add.tierId;
      return { id: Number(add.variantId), quantity: 1, properties: props };
    }

    if (plan.adds.length) {
      // All missing gifts go in ONE /cart/add.js call (each with its own tier
      // property). Shopify rejects the whole batch if ANY item is unavailable,
      // so on failure retry one-by-one to add what's possible and put only the
      // real offenders on cooldown.
      var batchOk = await run("/cart/add.js", { items: plan.adds.map(toAddItem) }, function () {
        countDelta += plan.adds.length;
      });
      if (!batchOk) {
        for (var k = 0; k < plan.adds.length; k++) {
          var add = plan.adds[k];
          var ok = await run("/cart/add.js", { items: [toAddItem(add)] }, function () {
            countDelta += 1;
          });
          if (!ok) {
            // Sold out / unavailable — back off so we don't retry on every write.
            failedAdds[String(add.variantId)] = Date.now();
          }
        }
      }
    }

    return { mutated: mutated, last: last, countDelta: countDelta };
  }

  // Paint the drawer from the gift write's own response when possible; fall
  // back to the engine's full recovery re-fetch otherwise.
  async function paintResult(cart, result) {
    var last = result.last;
    var sections = last && last.sections;
    if (sections && typeof window.__cartDrawerApplySections === "function") {
      // /cart/change responses are the full cart (item_count present);
      // /cart/add responses aren't — derive the count from what we did. Only
      // the empty/non-empty distinction matters to the applier's guards.
      var count =
        last && typeof last.item_count === "number"
          ? last.item_count
          : Math.max(0, (cart.item_count || 0) + result.countDelta);
      window.__cartDrawerApplySections({ item_count: count, sections: sections });
      return;
    }
    if (typeof window.__cartDrawerRecover === "function") {
      await window.__cartDrawerRecover();
    }
  }

  async function syncFreeGifts(snapshot) {
    if (syncInFlight) {
      resyncQueued = true;
      return;
    }
    var cfg = getConfig();
    if (!cfg) return; // section not on the page / bad JSON — never touch the cart

    syncInFlight = true;
    try {
      // A full-cart response body observed by the wrapper doubles as the state
      // read — skip the /cart.js round trip entirely for those triggers.
      var cart = looksLikeFullCart(snapshot) ? snapshot : await fetchCart();
      // Tag rules need each product's tags — cached, fetched only when used.
      await ensureTags(cart, cfg.tiers);
      var plan = buildPlan(cart, cfg.tiers);
      var hasWork = plan.removes.length || plan.qtyFixes.length || plan.adds.length;

      if (hasWork) {
        var result = await applyPlan(plan);
        if (result.mutated) {
          await paintResult(cart, result);
          // Grid steppers resync their line-key map from /cart.js on this event.
          document.dispatchEvent(new CustomEvent("cart:updated", { detail: {} }));
        }
        chainCount += 1;
      } else {
        chainCount = 0;
      }
    } catch (e) {
      // Network hiccup — the next cart mutation retries naturally.
    } finally {
      syncInFlight = false;
      if (resyncQueued) {
        resyncQueued = false;
        if (chainCount < MAX_CHAIN) scheduleSync(null);
        else chainCount = 0; // circuit breaker: something is fighting us — stop
      }
    }
  }

  function scheduleSync(cartSnapshot) {
    if (looksLikeFullCart(cartSnapshot)) pendingSnapshot = cartSnapshot; // latest wins
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      var snap = pendingSnapshot;
      pendingSnapshot = null;
      syncFreeGifts(snap);
    }, SYNC_COALESCE_MS);
  }

  // ---- Observe every cart write on the page --------------------------------

  function isCartWriteUrl(url) {
    if (!url) return false;
    var path;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch (e) {
      path = String(url).split("?")[0];
    }
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
  }

  function requestHasGiftHeader(input, init) {
    try {
      var initHeaders = init && init.headers ? new Headers(init.headers) : null;
      var inputHeaders = input && input.headers ? new Headers(input.headers) : null;
      return Boolean(
        (initHeaders && initHeaders.get(GIFT_HEADER)) ||
          (inputHeaders && inputHeaders.get(GIFT_HEADER))
      );
    } catch (e) {
      return false;
    }
  }

  if (window.fetch && !window.fetch.__freeGiftWrapped) {
    var originalFetch = window.fetch;
    var wrappedFetch = function (input) {
      var init = arguments[1];
      var url = typeof input === "string" ? input : input && input.url;
      var watch = isCartWriteUrl(url) && !requestHasGiftHeader(input, init);
      var promise = originalFetch.apply(this, arguments);
      if (watch) {
        promise
          .then(function (response) {
            if (!response || !response.ok) return;
            // Read the response body (clone — never consume the caller's copy):
            // change/update answers ARE the post-write cart, letting the sync
            // start from that state instantly instead of re-fetching /cart.js.
            response
              .clone()
              .json()
              .then(function (body) {
                scheduleSync(looksLikeFullCart(body) ? body : null);
              })
              .catch(function () {
                scheduleSync(null);
              });
          })
          .catch(function () {});
      }
      return promise;
    };
    wrappedFetch.__freeGiftWrapped = true;
    window.fetch = wrappedFetch;
  }

  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__freeGiftWrapped) {
    var originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__freeGiftIsCartWrite = isCartWriteUrl(url);
      return originalOpen.apply(this, arguments);
    };
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__freeGiftIsCartWrite) {
        this.addEventListener("load", function () {
          if (this.status < 200 || this.status >= 300) return;
          var body = null;
          try {
            body = JSON.parse(this.responseText);
          } catch (e) {
            // Non-JSON response — fall back to a /cart.js read.
          }
          scheduleSync(looksLikeFullCart(body) ? body : null);
        });
      }
      return originalSend.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__freeGiftWrapped = true;
  }

  // Belt-and-braces: theme-level cart events (grid stepper, managed drawer
  // adds) — the stepper event even carries the fresh cart JSON.
  document.addEventListener("grid-stepper:cart-changed", function (event) {
    var detail = (event && event.detail) || {};
    var cart = detail.cart !== undefined ? detail.cart : detail;
    scheduleSync(looksLikeFullCart(cart) ? cart : null);
  });
  document.addEventListener("theme:cart:added", function () {
    scheduleSync(null);
  });

  // Initial reconcile — covers page loads where the cart already qualifies
  // (e.g. gift tier added by the merchant after items were in the cart).
  if (document.readyState !== "loading") {
    scheduleSync(null);
  } else {
    document.addEventListener("DOMContentLoaded", function () {
      scheduleSync(null);
    });
  }
})();
